/**
 * Sprint 2b risk state: volatility scenario assignments, rug pull events,
 * rolling price samples, rug insurance and trade rewind.
 * Pure data only (no window/document/PIXI).
 */
import { COINS } from './config/coins.js';
import { SCENARIO_CONFIG } from './config/scenarios.js';
import type { ExchangeHolding } from './exchangeState.js';

export type RugPhase = 'none' | 'crashing' | 'floor' | 'recovering';

export interface RugPullState {
  phase: RugPhase;
  startedAt: number;
  phaseEndsAt: number;
  /** pre-crash price (panic exit reference, insurance valuation) */
  startPrice: number;
  /** crash bottom the price slides toward */
  targetPrice: number;
  /** panic exit already used for this rug event */
  panicUsed: boolean;
  /** newest pump-end event already handled (rug roll done) */
  lastPumpEventAt: number;
}

export interface ScenarioAssignState {
  scenarioId: string | null;
  startedAt: number;
  /** when idle: next assignment time */
  nextAt: number;
  /** last time a pump-ending scenario finished (rug trigger source) */
  pumpEndedAt: number;
}

export interface PriceSample {
  t: number;
  p: number;
}

export interface InsurancePolicy {
  activatedAt: number;
  expiresAt: number;
}

export interface InsurancePayout {
  coinId: string;
  amount: number;
  at: number;
}

export interface LastTradeState {
  coinId: string;
  side: 'buy' | 'sell';
  /** execution price */
  price: number;
  /** coin amount bought/sold */
  amount: number;
  /** game coins spent (buy) or received (sell) */
  cash: number;
  /** realized profit booked by the trade (sell only) */
  realizedProfit: number;
  /** holding before the trade (restored on rewind) */
  prevHolding: ExchangeHolding | null;
  at: number;
}

export interface RiskState {
  scenarios: Record<string, ScenarioAssignState>;
  rugs: Record<string, RugPullState>;
  /** rolling ~50s price history per meme coin (panic exit reference) */
  samples: Record<string, PriceSample[]>;
  lastSampleAt: number;
  insurance: Record<string, InsurancePolicy>;
  lastInsurancePayout: InsurancePayout | null;
  /** panic exit lock per coin (coinId -> until timestamp) */
  panicLockUntil: Record<string, number>;
  lastTrade: LastTradeState | null;
  /** trade rewind available again at this time (20h cooldown) */
  rewindReadyAt: number;
}

function randRange(min: number, max: number): number {
  return min + Math.random() * (max - min);
}

export function freshRug(): RugPullState {
  return {
    phase: 'none',
    startedAt: 0,
    phaseEndsAt: 0,
    startPrice: 0,
    targetPrice: 0,
    panicUsed: false,
    lastPumpEventAt: 0,
  };
}

export function freshScenario(now: number): ScenarioAssignState {
  return {
    scenarioId: null,
    startedAt: 0,
    nextAt: now + randRange(SCENARIO_CONFIG.firstDelayMin, SCENARIO_CONFIG.firstDelayMax),
    pumpEndedAt: 0,
  };
}

export function createInitialRiskState(): RiskState {
  const now = Date.now();
  const scenarios: Record<string, ScenarioAssignState> = {};
  const rugs: Record<string, RugPullState> = {};
  for (const cfg of COINS) {
    if (cfg.tier !== 'meme') continue;
    scenarios[cfg.id] = freshScenario(now);
    rugs[cfg.id] = freshRug();
  }
  return {
    scenarios,
    rugs,
    samples: {},
    lastSampleAt: 0,
    insurance: {},
    lastInsurancePayout: null,
    panicLockUntil: {},
    lastTrade: null,
    rewindReadyAt: 0,
  };
}

function migrateRug(saved: Partial<RugPullState> | undefined): RugPullState {
  const fresh = freshRug();
  if (!saved || typeof saved.phase !== 'string') return fresh;
  return {
    phase: saved.phase,
    startedAt: saved.startedAt ?? 0,
    phaseEndsAt: saved.phaseEndsAt ?? 0,
    startPrice: saved.startPrice ?? 0,
    targetPrice: saved.targetPrice ?? 0,
    panicUsed: saved.panicUsed ?? false,
    lastPumpEventAt: saved.lastPumpEventAt ?? 0,
  };
}

function migrateScenario(saved: Partial<ScenarioAssignState> | undefined, now: number): ScenarioAssignState {
  const fresh = freshScenario(now);
  if (!saved) return fresh;
  return {
    scenarioId: saved.scenarioId ?? null,
    startedAt: saved.startedAt ?? 0,
    nextAt: saved.nextAt ?? fresh.nextAt,
    pumpEndedAt: saved.pumpEndedAt ?? 0,
  };
}

/** Migrate a loaded save: fill missing fields with defaults (backward compatible). */
export function migrateRiskState(saved: Partial<RiskState> | undefined): RiskState {
  const fresh = createInitialRiskState();
  if (!saved) return fresh;
  const now = Date.now();
  const scenarios: Record<string, ScenarioAssignState> = {};
  const rugs: Record<string, RugPullState> = {};
  for (const coinId of Object.keys(fresh.scenarios)) {
    scenarios[coinId] = migrateScenario(saved.scenarios?.[coinId], now);
    rugs[coinId] = migrateRug(saved.rugs?.[coinId]);
  }
  return {
    scenarios,
    rugs,
    // Samples go stale across sessions - always restart the rolling window
    samples: {},
    lastSampleAt: 0,
    insurance: saved.insurance ?? {},
    lastInsurancePayout: saved.lastInsurancePayout ?? null,
    panicLockUntil: saved.panicLockUntil ?? {},
    lastTrade: saved.lastTrade ?? null,
    rewindReadyAt: saved.rewindReadyAt ?? 0,
  };
}
